import React, { useState, useEffect } from 'react';
import { Printer, Usb, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as printerApi from '@/services/printerApi';
import PrintMethodToggle from '@/components/print/PrintMethodToggle';
import { generateTsplFromLabelData, PRINTER_CONFIGS } from '@/utils/tsplGenerator';

/**
 * FG Sticker (Main Label) print dialog for FOB USN Tagging.
 * Produces the SAME output as SFG Main Label Printing by reusing the shared
 * `generateTsplFromLabelData` util + `/main-labels/preview-batch` label data.
 *
 * items: [{ usn_code, fg_barcode }]
 */
const FGStickerPrintDialog = ({ open, onOpenChange, items = [], title = 'Print FG Stickers', onPrinted = null }) => {
  const [templates, setTemplates] = useState([]);
  const [pricingType, setPricingType] = useState('TN');
  const [printerModel, setPrinterModel] = useState('TSC_TE210');
  const [copiesPerLabel, setCopiesPerLabel] = useState(1);
  const [isPrinting, setIsPrinting] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (open) {
      fetchTemplates();
      setStatus('');
    }
  }, [open]);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/main-labels/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load label templates');
    }
  };

  // Resolve a template by name (case-insensitive, partial) — mirrors SFG Main
  // Label Printing so each SKU (Dhoti / Standard / Combo) uses its own layout.
  const findTemplateByName = (templateName) => {
    if (!templateName || !templates.length) return null;
    const t = templateName.toLowerCase();
    return templates.find(tpl =>
      tpl.template_name?.toLowerCase() === t ||
      tpl.template_name?.toLowerCase().includes(t) ||
      t.includes(tpl.template_name?.toLowerCase())
    ) || null;
  };

  const validItems = (items || []).filter(i => i.usn_code && i.fg_barcode);
  const missingBarcodeCount = (items || []).length - validItems.length;

  const handlePrint = async () => {
    if (validItems.length === 0) {
      toast.error('No items with a valid FG barcode to print');
      return;
    }

    setIsPrinting(true);
    try {
      const printerConfig = PRINTER_CONFIGS[printerModel];
      const barcodes = [...new Set(validItems.map(i => i.fg_barcode))];

      setStatus(`Fetching label data for ${barcodes.length} FG barcode(s)...`);
      const batchResponse = await apiClient.post('/main-labels/preview-batch', {
        barcodes,
        pricing_type: pricingType,
        template_id: templates[0]?.template_id,
      });
      const labelDataMap = batchResponse.data.results || {};
      const defaultTemplate = batchResponse.data.template || templates[0] || null;

      let allCommands = '';
      let totalLabels = 0;
      let skipped = 0;

      setStatus(`Generating ${validItems.length} FG sticker(s)...`);
      for (const item of validItems) {
        const cached = labelDataMap[item.fg_barcode];
        if (!cached) {
          skipped += 1;
          continue;
        }
        const labelData = cached.label_data || {};
        const mrpFormatted = cached.mrp_formatted || `Rs.${labelData.mrp || 0}`;
        // Pick this SKU's own template (Dhoti / Standard / Combo) so the black
        // text aligns with the pre-printed label stock. Falls back to default.
        const template = findTemplateByName(cached.print_template) || defaultTemplate;
        for (let c = 0; c < copiesPerLabel; c++) {
          allCommands += generateTsplFromLabelData(
            labelData,
            template,
            item.usn_code,
            mrpFormatted,
            null,
            printerConfig,
            template?.template_type
          );
          totalLabels += 1;
        }
      }

      if (!allCommands) {
        throw new Error(`No printable FG stickers — ${skipped} item(s) had no label data. Check FG barcode / item master.`);
      }

      setStatus(`Sending ${totalLabels} label(s) to printer...`);
      const device = await printerApi.requestUSBPrinter();
      await printerApi.sendToUSBPrinter(device, allCommands);

      const skipMsg = skipped > 0 ? ` (${skipped} skipped — no label data)` : '';
      toast.success(`Sent ${totalLabels} FG sticker(s)${skipMsg}`);
      if (typeof onPrinted === 'function') { try { onPrinted(); } catch (e) { /* non-fatal */ } }
      onOpenChange(false);
    } catch (error) {
      console.error('FG sticker print error:', error);
      toast.error(error.message || 'Failed to print FG stickers');
    } finally {
      setIsPrinting(false);
      setStatus('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="fg-sticker-print-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-[#2D5F4F]" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Prints the FG main label (same as SFG Main Label Printing) for each piece via USB thermal printer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="p-3 bg-[#F5F1E8] rounded-lg text-sm">
            <div className="flex justify-between">
              <span className="text-[#5A5F6E]">FG stickers to print:</span>
              <strong className="text-[#2D5F4F]">{validItems.length}</strong>
            </div>
            {missingBarcodeCount > 0 && (
              <div className="flex justify-between mt-1 text-amber-700">
                <span>Missing FG barcode:</span>
                <strong>{missingBarcodeCount}</strong>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Pricing Type</Label>
              <Select value={pricingType} onValueChange={setPricingType}>
                <SelectTrigger className="mt-1" data-testid="fg-pricing-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TN">TN</SelectItem>
                  <SelectItem value="WHT">WHT</SelectItem>
                  <SelectItem value="MRP">MRP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Copies / Label</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={copiesPerLabel}
                onChange={(e) => setCopiesPerLabel(Math.max(1, parseInt(e.target.value) || 1))}
                className="mt-1"
                data-testid="fg-copies-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Printer Model</Label>
            <Select value={printerModel} onValueChange={setPrinterModel}>
              <SelectTrigger className="mt-1" data-testid="fg-printer-model">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PRINTER_CONFIGS).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {status && (
            <p className="text-sm text-blue-600 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> {status}
            </p>
          )}

          <PrintMethodToggle screenKey="fg-sticker" className="w-full justify-center" />

          <Button
            onClick={handlePrint}
            disabled={isPrinting || validItems.length === 0}
            className="w-full bg-[#2D5F4F] hover:bg-[#234A3D]"
            data-testid="fg-sticker-connect-print-btn"
          >
            {isPrinting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Usb className="w-4 h-4 mr-2" />
            )}
            Print
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FGStickerPrintDialog;
